import * as React from "react";
import uniqueId from "lodash/uniqueId";
import styled from "@emotion/styled";
import { useToggle } from "utils/hooks";
import { IconChevron } from "./icons";

type ExpandContentProps = {
  linesVisible?: number;
  lineHeight?: number;
  strict?: boolean;
  showLabel?: React.ReactNode;
  hideLabel?: React.ReactNode;
  chevron?: boolean;
  children: React.ReactNode;
};

const getFontSize = (element: HTMLElement | null) => {
  if (!element) return null;

  const childElements = [...element.children].filter(
    (child) => child instanceof HTMLElement
  );

  if (childElements[0]) {
    return window.getComputedStyle(childElements[0]).fontSize;
  }

  return window.getComputedStyle(element).fontSize;
};

function ExpandContent({
  linesVisible = 3,
  lineHeight = 1.4,
  strict = false,
  showLabel = "Expand content",
  hideLabel = "Collapse content",
  chevron = true,
  children,
  ...props
}: ExpandContentProps) {
  const [fontSize, setFontSize] = React.useState(14);

  const targetHeight = lineHeight * linesVisible * fontSize;
  const threshold = fontSize * lineHeight * 2;

  const [textHeight, setTextHeight] = React.useState(0);
  const ref = React.useRef<HTMLDivElement | null>(null);

  const [isOpen, toggle] = useToggle(false);
  const hideLabelNew = hideLabel || showLabel;
  const [id] = React.useState(uniqueId());

  // Checks font size
  React.useLayoutEffect(() => {
    setFontSize(Number(getFontSize(ref?.current)?.replace("px", "")));
  }, []);

  // Sets isActive to false when the height of the text is less than the threshold
  React.useLayoutEffect(() => {
    setTextHeight(
      (prev) => ref?.current?.getBoundingClientRect().height ?? prev
    );
  }, []);

  const shouldHide = strict
    ? textHeight >= targetHeight
    : textHeight >= targetHeight + threshold;

  return (
    <ExpandContentWrapper {...props}>
      <ExpandContentText
        id={id}
        isOpen={isOpen}
        lineHeight={lineHeight}
        linesVisible={linesVisible}
        shouldHide={shouldHide}
      >
        <div ref={ref}>{children}</div>
      </ExpandContentText>

      {shouldHide && (
        <ExpandContentButton
          onClick={toggle}
          isOpen={isOpen}
          aria-controls={id}
          aria-label={isOpen ? "Collapse content" : "Expand content"}
          aria-expanded={isOpen ? "true" : "false"}
        >
          {chevron && <IconChevron size=".75em" className="chevron" />}{" "}
          {isOpen ? hideLabelNew : showLabel}
        </ExpandContentButton>
      )}
    </ExpandContentWrapper>
  );
}

const ExpandContentWrapper = styled.div({
  position: "relative",
  marginBottom: 5,
});

const ExpandContentText = styled.div<{
  lineHeight: number;
  isOpen: boolean;
  shouldHide: boolean;
  linesVisible: number;
  strict?: boolean;
}>(({ isOpen, lineHeight, linesVisible, shouldHide }) => ({
  ...(shouldHide && {
    "&>div": {
      ...(!isOpen && {
        display: "-webkit-box",
        textOverflow: "ellipsis",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: `${linesVisible}`,
        "*": {
          margin: 0,
          display: "inline",
        },
      }),
    },
  }),
  lineHeight,
  overflow: "hidden",
  "&>div>*:first-of-type": {
    lineHeight,
  },
  "p:last-of-type, ul:last-of-type": {
    marginBottom: 0,
  },
}));

const ExpandContentButton = styled.button<{ isOpen: boolean }>(
  ({ isOpen, theme }) => ({
    backgroundColor: "rgb(0 0 0 / 0)",
    border: 0,
    borderRadius: 0,
    color: theme.colors.fg,
    cursor: "pointer",
    fontSize: theme.fontSizes.xxs,
    marginTop: ".66rem",
    opacity: ".66",
    padding: 0,
    ".chevron": {
      transform: isOpen ? "rotate(0)" : "rotate(180deg) translateY(-2px)",
    },
    "&:hover": {
      opacity: 1,
    },
  })
);

export { ExpandContent };
